import type { ClinicalActionAvailability } from '@dermloop/static-values';
import type { ClinicalFormPermissions } from '@dermloop/utils';
import { Type } from 'class-transformer';
import {
  IsArray,
  IsNotEmpty,
  IsUUID,
  IsObject,
  IsOptional,
  IsString,
  ValidateNested,
} from 'class-validator';
import { BaseCreateDto } from '../base.dto';
import {
  ClinicalActionDto,
  CreateClinicalActionOccurrenceDto,
} from '../forms/clinical-action.dtos';
import { FormSubmissionBlobDto } from '../forms/form-submission.dtos';

export class SaveClinicalRevisionDraftDto extends BaseCreateDto {
  @IsObject()
  @ValidateNested()
  @Type(() => FormSubmissionBlobDto)
  clinical: FormSubmissionBlobDto;
}

export class StartClinicalRevisionDraftDto {
  /** The Epic encounter the edit is made in; defaults to the predecessor's. */
  @IsOptional()
  @IsString()
  @IsNotEmpty()
  encounterFhirReference?: string;
}

export class CompleteClinicalRevisionDto {
  /** New actions on the information this completion produces, created together with it or not at all. */
  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => CreateClinicalActionOccurrenceDto)
  actions?: CreateClinicalActionOccurrenceDto[];

  /** Actions of this case to cancel or remove in the same save. */
  @IsOptional()
  @IsArray()
  @IsUUID('all', { each: true })
  removeActionIds?: string[];

  /** The Epic encounter `actions` are taken in. */
  @IsOptional()
  @IsString()
  @IsNotEmpty()
  encounterFhirReference?: string;
}

export interface ClinicalRevisionContextDto {
  triageId: string;
  formSubmissionId: string;
  formTemplateVersionId: string;
  document: object;
  submission: FormSubmissionBlobDto;
  draft: boolean;
  predecessorFormSubmissionId: string | null;
  /** The caller's current rights; presentation only, rechecked on every write. */
  permissions: ClinicalFormPermissions;
  /** Every configured option, and whether the caller can add it now; clients hide the rest. */
  actionAvailability: ClinicalActionAvailability[];
}

export interface CompleteClinicalRevisionResultDto {
  triageId: string;
  formSubmissionId: string;
  unchanged: boolean;
  actions: ClinicalActionDto[];
}
